import React, { useEffect, useState } from "react";
import AppLayout from "@/components/AppLayout";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "@/components/ui/tabs";
import { Wallet, Building2, HeartHandshake, Calculator, SlidersHorizontal } from "lucide-react";
import { useAuth } from "@/lib/auth";
import EarningsCalculatorTab from "@/pages/EarningsCalculatorTab";
import RateCardsTab from "@/pages/RateCardsTab";

const ADMIN_ROLES = ["super_admin", "founder", "finance"];

export default function Commissions() {
  const { user } = useAuth();
  const isAdmin = ADMIN_ROLES.includes(user?.role);
  const [data, setData] = useState(null);
  const [tab, setTab] = useState(isAdmin ? "ledger" : "calculator");

  useEffect(() => {
    if (isAdmin) {
      api.get("/commissions/summary").then(r => setData(r.data)).catch(() => {});
    }
  }, [isAdmin]);

  return (
    <AppLayout title="Commissions">
      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList className="mb-4" data-testid="commissions-tabs">
          {isAdmin && (
            <TabsTrigger value="ledger" data-testid="tab-ledger">
              <Wallet size={14} className="mr-1"/> Ledger
            </TabsTrigger>
          )}
          <TabsTrigger value="calculator" data-testid="tab-calculator">
            <Calculator size={14} className="mr-1"/> Earnings Calculator
          </TabsTrigger>
          {isAdmin && (
            <TabsTrigger value="rate-cards" data-testid="tab-rate-cards">
              <SlidersHorizontal size={14} className="mr-1"/> Rate Cards
            </TabsTrigger>
          )}
        </TabsList>

        {isAdmin && (
          <TabsContent value="ledger">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5">
              <BigCard label="Grand total commission due"
                       value={data ? `₹${(data.grand_total).toLocaleString("en-IN")}` : "—"}
                       accent="#5FBF3A" primary/>
              <BigCard label="Franchise commissions"
                       value={data ? `₹${(data.franchise_total).toLocaleString("en-IN")}` : "—"}
                       accent="#0EA5FF"/>
              <BigCard label="External partner commissions"
                       value={data ? `₹${(data.external_total).toLocaleString("en-IN")}` : "—"}
                       accent="#041B46"/>
            </div>
            <SectionTable title="Franchise partners" icon={Building2}
                          rows={data?.franchise_rows || []} subKey="city"/>
            <div className="h-6"/>
            <SectionTable title="External partners" icon={HeartHandshake}
                          rows={data?.external_rows || []} subKey="organisation"/>
          </TabsContent>
        )}

        <TabsContent value="calculator">
          <EarningsCalculatorTab/>
        </TabsContent>

        {isAdmin && (
          <TabsContent value="rate-cards">
            <RateCardsTab/>
          </TabsContent>
        )}
      </Tabs>
    </AppLayout>
  );
}

function BigCard({ label, value, accent, primary }) {
  return (
    <div className={`miro-card p-5 ${primary ? "relative overflow-hidden" : ""}`}
         style={primary ? { background: "var(--miro-navy)", color: "#fff" } : {}}>
      {primary && <div className="absolute inset-0" style={{ background: "radial-gradient(300px at 90% 20%, rgba(14,165,255,0.35), transparent 60%)" }}/>}
      <div className="relative z-10">
        <div className="miro-overline" style={primary ? { color: "#B7C4DE" } : {}}>{label}</div>
        <div className="font-display font-black text-3xl mt-2" style={{ color: primary ? "#fff" : "var(--miro-navy)" }}>{value}</div>
      </div>
    </div>
  );
}

function SectionTable({ title, icon: Icon, rows, subKey }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <Icon size={17} className="text-[var(--miro-navy)]"/>
        <div className="font-display font-bold text-lg text-[var(--miro-navy)]">{title}</div>
        <Badge className="border-0 font-mono" style={{ background: "#F1F5F9", color: "#475569" }}>{rows.length}</Badge>
      </div>
      <div className="bg-white border border-[var(--miro-border)] rounded-xl overflow-x-auto">
        <table data-testid={`commission-table-${title.toLowerCase().split(" ")[0]}`} className="w-full text-sm">
          <thead className="bg-[var(--miro-bg)] text-[var(--miro-text-secondary)]">
            <tr className="text-left">
              <th className="px-4 py-3 font-semibold">Partner</th>
              <th className="px-4 py-3 font-semibold">Rate</th>
              <th className="px-4 py-3 font-semibold text-right">Won revenue</th>
              <th className="px-4 py-3 font-semibold text-right">Commission due</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={4} className="px-4 py-12 text-center text-[var(--miro-text-muted)]">Nothing to settle yet.</td></tr>
            )}
            {rows.map(r => (
              <tr key={r.id} data-testid={`commission-row-${r.id}`} className="border-t border-[var(--miro-border)]">
                <td className="px-4 py-3">
                  <div className="font-semibold text-[var(--miro-navy)]">{r.name}</div>
                  <div className="text-xs text-[var(--miro-text-muted)]">{r[subKey] || "—"}</div>
                </td>
                <td className="px-4 py-3 font-mono text-[var(--miro-navy)]">{r.rate}%</td>
                <td className="px-4 py-3 text-right font-mono text-[var(--miro-navy)]">₹{(r.won_revenue || 0).toLocaleString("en-IN")}</td>
                <td className="px-4 py-3 text-right font-mono font-semibold text-[var(--miro-green-600)]">₹{(r.commission_due || 0).toLocaleString("en-IN")}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
